import { inject, Injectable } from '@angular/core';
import { API_CONFIG, ApiCallingV2Service, DropdownData } from '@zayan-erp/shared';
import { map, Observable, of } from 'rxjs';
import { MajorCode } from '../models/major-code.model';
import { MajorCodeType } from '../enums/major-codes.enum';
import { MajorMinors } from '../models/major-minors.model';

@Injectable({
  providedIn: 'root'
})
export class MajorCodeService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  readMajorCodes(jobTypeId?: string): Observable<MajorCode[]> {
    const url = jobTypeId ? `${this.baseUrl}/MajorCode?jobTypeId=${jobTypeId}` : `${this.baseUrl}/MajorCode`;
    return this.#http.getData(url, { showLoader: true, showMessage: false });
  }

  readMajorCodeById(id: string): Observable<MajorCode> {
    return this.#http.getData(`${this.baseUrl}/MajorCode/${id}`, { showLoader: true, showMessage: false });
  }

  createMajorCode(majorCode: Omit<MajorCode, 'id'>): Observable<any> {
    return this.#http.postData(`${this.baseUrl}/MajorCode`, { showLoader: true, showMessage: true }, majorCode);
  }

  updateMajorCode(majorCode: Omit<MajorCode, 'id'>, id: string): Observable<any> {
    return this.#http.putData(`${this.baseUrl}/MajorCode/${id}`, majorCode, { showLoader: true, showMessage: true });
  }

  deleteMajorCode(id: string): Observable<any> {
    return this.#http.deleteData(`${this.baseUrl}/MajorCode/${id}`, { showLoader: true, showMessage: true });
  }

  getMajorCodeStatusList(): Observable<DropdownData[]> {
    const list = Object.entries(MajorCodeType)
      .filter(([key]) => isNaN(Number(key)))
      .map(([key, value]) => ({ id: String(value), firstLabel: key, secondLabel: null }));
    return of(list);
  }

  readUnitOfMeasures(): Observable<DropdownData[]> {
    return this.#http.getData(`${this.baseUrl}/lookup/uofms`, { showLoader: true, showMessage: false }).pipe(
      map(list =>
        (list || []).map(
          (item: any) =>
            ({
              id: item.id,
              firstLabel: item.code,
              secondLabel: item.description ?? ''
            }) as DropdownData
        )
      )
    );
  }

  assignMinorCode(majorCodeId: string, minorCodeIds: string[]): Observable<any> {
    const payload = {
      majorCodeId,
      minorCodeIds
    };
    return this.#http.postData(`${this.baseUrl}/MajorMinorList`, { showLoader: true, showMessage: true }, payload);
  }

  readMajorMinors(majorCodeId: string): Observable<MajorMinors[]> {
    return this.#http.getData(`${this.baseUrl}/MajorMinorList/majorcode/${majorCodeId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  removeMinorCode(majorCodeId: string, minorCodeId: string): Observable<any> {
    const payload = {
      majorCodeId,
      minorCodeId
    };
    return this.#http.postData(
      `${this.baseUrl}/MajorMinorList/delete`,
      { showLoader: true, showMessage: true },
      payload
    );
  }
}
